import React, { useMemo } from 'react';
import { Typography } from 'antd';
import { Area } from '@ant-design/charts';
import type { HourPoint, TodayMetric } from '@/types';

export interface TodayMiniChartProps {
  today: HourPoint[];
  yesterday: HourPoint[];
  metric: TodayMetric;
  /** Multiplies every value (e.g. the net share, to chart IAP net from gross). */
  scale?: number;
  /** Tooltip value formatter. */
  format: (n: number) => string;
  /**
   * What the chart plots when that is not the tile's headline number, e.g.
   * "IAP net" on the Money tile: shown above the chart and in the tooltip.
   */
  label?: string;
}

type Series = 'Today' | 'Yesterday';

interface Datum {
  /** IST hour label, e.g. "14:00". */
  hour: string;
  value: number;
  series: Series;
}

const TODAY_COLOR = '#2563eb';
const YESTERDAY_COLOR = '#94a3b8';
const CHART_HEIGHT = 96;
const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;

/*
 * Everything below that does not depend on props lives at module level:
 * @ant-design/charts deep-compares its config on every render, and a stable
 * config plus `animate: false` keeps the 60 s refresh from replaying the draw
 * animation or flickering.
 */
const SCALE = {
  color: { domain: ['Today', 'Yesterday'], range: [TODAY_COLOR, YESTERDAY_COLOR] },
};
const AXIS = {
  x: { labelAutoRotate: false, labelAutoHide: true, tickCount: 5, title: false },
  y: false as const,
};
// `animate` is a real G2 option that @ant-design/plots copies onto every mark,
// but its prop types do not list it, so it goes in through a spread.
const NO_ANIMATION = { animate: false };
const isYesterday = (g: Datum[]) => g[0]?.series === 'Yesterday';
const colorOf = (g: Datum[]) => (isYesterday(g) ? YESTERDAY_COLOR : TODAY_COLOR);
const STYLE = {
  fill: colorOf,
  // Yesterday is a line only; today carries the soft fill.
  fillOpacity: (g: Datum[]) => (isYesterday(g) ? 0 : 0.2),
  connect: true,
};
const LINE = {
  style: {
    stroke: colorOf,
    lineWidth: (g: Datum[]) => (isYesterday(g) ? 1.5 : 2),
    lineDash: (g: Datum[]) => (isYesterday(g) ? [4, 4] : []),
    connect: true,
  },
};

const TodayMiniChartBase: React.FC<TodayMiniChartProps> = ({
  today,
  yesterday,
  metric,
  scale = 1,
  format,
  label,
}) => {
  const data = useMemo<Datum[]>(() => {
    const rows = (points: HourPoint[], series: Series): Datum[] =>
      points.map((p) => ({ hour: hourLabel(p.hour), value: p.cum[metric] * scale, series }));
    return [...rows(yesterday, 'Yesterday'), ...rows(today, 'Today')];
  }, [today, yesterday, metric, scale]);

  const tooltip = useMemo(
    () => ({
      title: (d: Datum) => `Through ${d.hour.slice(0, 2)}:59 IST`,
      items: [
        (d: Datum) => ({
          name: label ? `${label} ${d.series.toLowerCase()}` : d.series,
          value: format(d.value),
        }),
      ],
    }),
    [format, label]
  );

  // Redis unreadable: the API sends empty arrays. Nothing to draw, nothing to crash on.
  if (data.length === 0) return null;

  const chart = (
    <Area
      data={data}
      xField="hour"
      yField="value"
      seriesField="series"
      height={CHART_HEIGHT}
      {...NO_ANIMATION}
      legend={false}
      scale={SCALE}
      axis={AXIS}
      style={STYLE}
      line={LINE}
      tooltip={tooltip}
    />
  );
  if (!label) return chart;
  return (
    <>
      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 6 }}>
        {label}
      </Typography.Text>
      {chart}
    </>
  );
};

export const TodayMiniChart = React.memo(TodayMiniChartBase);
